import { useEffect, useState } from "react";
import { Loader2, TrendingUp, DollarSign, Repeat, Users, AlertTriangle, Info } from "lucide-react";
import api from "@/lib/api";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function Metric({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="kpi-lux p-4" data-testid={testid}>
      <div className="relative flex items-center gap-2 text-slate-400"><Icon size={15} className="text-[#8fb4ff]" /><span className="font-mono text-[10px] font-bold uppercase tracking-wider">{label}</span></div>
      <div className="relative mt-1.5 font-mono text-3xl font-black tracking-tight text-white">{value}</div>
      {sub && <div className="relative mt-0.5 text-[12px] text-slate-400">{sub}</div>}
    </div>
  );
}

export default function RevenueIntelligence() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/owner/revenue").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);
  if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#1a5fd0]" /></div>;

  return (
    <div data-testid="revenue-intelligence">
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Revenue Intelligence</h1>
      <p className="mt-1 text-[13px] text-slate-500">Live recurring-revenue metrics computed from verified paid transactions only.</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric icon={DollarSign} label="MRR" value={money(d.mrr)} sub={`${money(d.arr)} ARR`} testid="rev-mrr" />
        <Metric icon={TrendingUp} label="New MRR (this month)" value={money(d.new_mrr_month)} testid="rev-new-mrr" />
        <Metric icon={Users} label="ARPU" value={money(d.arpu)} sub={`${d.active_subscriptions} active subs`} testid="rev-arpu" />
        <Metric icon={Repeat} label="Subscription revenue" value={money(d.subscription_revenue)} sub={`${money(d.one_time_revenue)} one-time`} testid="rev-sub" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="font-head text-lg font-extrabold text-slate-900">MRR by plan</h2>
          <div className="card-spotlight mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="mrr-by-plan">
            <table className="w-full text-left text-[13px]">
              <thead><tr className="border-b border-slate-200 bg-slate-50 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5">Plan</th><th className="px-4 py-2.5">Active</th><th className="px-4 py-2.5">MRR</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {["Track", "Build", "Expand"].map((p) => (
                  <tr key={p}><td className="px-4 py-2.5 font-semibold text-slate-800">{p}</td>
                    <td className="px-4 py-2.5 text-slate-600">{d.by_plan[p]?.active || 0}</td>
                    <td className="px-4 py-2.5 font-mono text-slate-800">{money(d.by_plan[p]?.mrr)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex gap-3">
            <div className="flex-1 rounded-xl border border-slate-200 bg-white p-3" data-testid="rev-failed">
              <div className="flex items-center gap-1.5 text-amber-600"><AlertTriangle size={14} /><span className="font-mono text-[10px] uppercase tracking-wider">Failed payments</span></div>
              <div className="mt-1 font-head text-xl font-extrabold text-slate-900">{d.failed_payments}</div>
            </div>
            <div className="flex-1 rounded-xl border border-slate-200 bg-white p-3" data-testid="rev-pastdue">
              <div className="flex items-center gap-1.5 text-amber-600"><AlertTriangle size={14} /><span className="font-mono text-[10px] uppercase tracking-wider">Past due</span></div>
              <div className="mt-1 font-head text-xl font-extrabold text-slate-900">{d.past_due}</div>
            </div>
            <div className="flex-1 rounded-xl border border-slate-200 bg-white p-3" data-testid="rev-recovered">
              <div className="flex items-center gap-1.5 text-emerald-600"><Repeat size={14} /><span className="font-mono text-[10px] uppercase tracking-wider">Recovered</span></div>
              <div className="mt-1 font-head text-xl font-extrabold text-slate-900">{money(d.recovered_revenue)}</div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-head text-lg font-extrabold text-slate-900">Cost intelligence</h2>
          <div className="card-spotlight mt-2 rounded-2xl border border-slate-200 bg-white p-4" data-testid="cost-intelligence">
            <div className="space-y-2 text-[13px]">
              {[["Provider APIs", d.costs.provider_cost], ["AI / LLM", d.costs.ai_cost], ["Infrastructure", d.costs.infra_cost]].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-slate-100 pb-1.5 last:border-0">
                  <span className="text-slate-600">{l}</span><span className="font-mono text-slate-500">{d.costs.instrumented ? money(v) : "—"}</span>
                </div>
              ))}
              <div className="flex justify-between pt-1"><span className="font-semibold text-slate-800">Gross margin</span><span className="font-mono text-slate-500">{d.gross_margin == null ? "—" : `${d.gross_margin}%`}</span></div>
            </div>
            <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
              <Info size={13} className="mt-0.5 shrink-0" /> {d.costs.note}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
